import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { IUserType, translate } from '../../libs/utils';
import { makeStyles } from '@mui/styles';
import {
  LIGHT_PURPLE,
  MAIN_LIGHT_COLOR,
  NOTICE,
  TYPOGRAPHY,
  WHITE,
} from '../../themes/colors';
import { TOPIC_LABEL } from '../../themes/fonts';
import { UserType } from '../../configs';
import { IArea, ICreateSafetyError } from '../../redux/safety/types';
import SelectAllAreasField from '../../components/questionnaire/SelectAllAreasField';
import Chip from '../../components/Chip';
import FormProvider from '../../components/FormProvider';
import { useForm } from 'react-hook-form';
import Button from '../../components/Button';
import TableButton from '../../components/table/TableButton';
import { useNavigate } from 'react-router-dom';
import { getAreas } from '../../redux/areas/action';
import { useAppDispatch } from '../../redux';
import { areasSelector } from '../../redux/areas/selectors';
import { useSelector } from 'react-redux';
import { ICreateSafetyLocal } from '../../libs/apis/safety/types';
import { createSafety } from '../../redux/safety/action';
import { errorsSelector } from '../../redux/safety/selectors';
import { clearCreateState, clearErrors } from '../../redux/safety';
import ErrorMessage from '../../components/ErrorMessage';
import CreatePopup from '../../components/popup/CreatePopup';

const useStyles = makeStyles({
  labelContainer: {
    minWidth: 'calc(241 / 1218 * 100%) !important',
    backgroundColor: MAIN_LIGHT_COLOR,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '18px',
    height: '100%',
  },

  textLabel: {
    color: WHITE,
    fontSize: `${TOPIC_LABEL} !important`,
    fontWeight: 'bold !important',
    textAlign: 'center',
  },

  inputField: {
    width: 'calc(718 / 959 * 100%)',
  },

  selectContainer: {
    width: '160px',
    marginLeft: '40px',
  },

  customSelectInput: {
    borderRadius: '12px !important',
    height: '50px !important',
  },
});

const memberTypes: IUserType[] = [
  {
    value: UserType.REGULAR,
    type: translate('member.list.table.membership_type.regular'),
  },
  {
    value: UserType.ASSOCIATE,
    type: translate('member.list.table.membership_type.associate'),
  },
  {
    value: UserType.ADMIN_STAFF,
    type: translate('member.list.table.membership_type.admin_staff'),
  },
  {
    value: UserType.ALL_MEMBER,
    type: translate('member.list.table.membership_type.all_member'),
  },
];

const defaultValue: ICreateSafetyLocal = {
  title: '',
  detail: '',
  user_type: 0,
  target_area: [],
  all_areas: false,
  is_open: false,
};

const CreateSafetyScreen: React.FC = () => {
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [areas, setAreas] = useState<IArea[]>([]);
  const [value, setValue] = useState<string>('');
  const [dataAdd, setDataAdd] = useState<ICreateSafetyLocal>(defaultValue);
  const [errorsMessage, setErrorsMessage] = useState<
    ICreateSafetyError | undefined
  >({});
  const [selectedAreas, setSelectedAreas] = useState<IArea[]>([]);
  const [isAllAreas, setIsAllAreas] = useState<boolean>(false);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const methods = useForm();
  const { handleSubmit } = methods;

  const areaData: IArea[] = useSelector(areasSelector);
  const errors: ICreateSafetyError | undefined = useSelector(errorsSelector);

  // eslint-disable-next-line no-undef
  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const key = e.target.name;
    setDataAdd({
      ...dataAdd,
      [key]: e.target.value,
    });
    const messages = { ...errorsMessage };
    if (key == 'title' && messages?.title) delete messages.title;
    if (key == 'detail' && messages?.detail) delete messages.detail;
    setErrorsMessage(messages);
  };

  const handleDeleteArea = (areaId: number) => {
    const selectedValue = [...selectedAreas];
    const area = selectedValue.find((item) => item.area_id == areaId);
    if (area) {
      const index = selectedValue.indexOf(area);
      selectedValue.splice(index, 1);
      setSelectedAreas(selectedValue);
      setIsAllAreas(false);

      setDataAdd({
        ...dataAdd,
        target_area: selectedValue.map((area) => area.area_id),
      });
    }
  };

  const onSubmit = handleSubmit(async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: any = await dispatch(createSafety(dataAdd));
    if (response.payload.success == true) {
      setOpenCreateModal(true);
      dispatch(clearErrors());
      dispatch(clearCreateState());
    }
  });

  const handleSelectAllArea = () => {
    const messages = { ...errorsMessage };
    if (messages?.target_area) {
      delete messages.target_area;
      setErrorsMessage(messages);
    }
    if (isAllAreas) {
      setSelectedAreas([]);
      setDataAdd({
        ...dataAdd,
        target_area: [],
        all_areas: false,
      });
    } else {
      setSelectedAreas([...areas]);
      setDataAdd({
        ...dataAdd,
        target_area: areas.map((area) => area.area_id),
        all_areas: true,
      });
    }
    setIsAllAreas(!isAllAreas);
  };

  useEffect(() => {
    dispatch(clearErrors());
    dispatch(clearCreateState());
    dispatch(getAreas());
  }, [dispatch]);

  useEffect(() => {
    setErrorsMessage(errors);
  }, [errors]);

  useEffect(() => {
    setAreas(areaData);
  }, [areaData]);

  const handleBackButton = () => navigate('/safety');

  const handleSelectArea = (event: SelectChangeEvent) => {
    setValue(event.target.value);
    const area = areas.find(
      (item) => item.area_id == parseInt(event.target.value),
    );
    if (area && !selectedAreas.find((item) => item.area_id == area.area_id)) {
      const selectedValue = selectedAreas;
      selectedValue.push(area);
      setSelectedAreas([...selectedValue]);

      const messages = { ...errorsMessage };
      if (messages?.target_area) {
        delete messages.target_area;
        setErrorsMessage(messages);
      }
      setDataAdd({
        ...dataAdd,
        target_area: selectedValue.map((area) => area.area_id),
      });
    }
  };

  const handleSelectMembershipType = (event: SelectChangeEvent) => {
    const messages = { ...errorsMessage };
    if (messages?.user_type) {
      delete messages.user_type;
      setErrorsMessage(messages);
    }
    setDataAdd({
      ...dataAdd,
      user_type: parseInt(event.target.value),
    });
  };

  return (
    <Layout title={translate('safety.create.title')}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Stack flexDirection="row" alignItems="center" sx={{ height: '82px' }}>
          <Box className={styles.labelContainer}>
            <Typography className={styles.textLabel}>
              {translate('safety.create.label.title')}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <TextField
              className={styles.inputField}
              id="title"
              name="title"
              type="text"
              variant="standard"
              value={dataAdd.title}
              error={!!errorsMessage?.title}
              onChange={handleOnChange}
            />
            {errorsMessage?.title && errorsMessage.title.length > 0 && (
              <Box sx={{ marginTop: '14px' }}>
                <ErrorMessage message={errorsMessage.title[0]} />
              </Box>
            )}
          </Box>
        </Stack>
        <Stack flexDirection="row" alignItems="center" sx={{ height: '140px' }}>
          <Box className={styles.labelContainer}>
            <Typography className={styles.textLabel}>
              {translate('safety.create.label.detail')}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1, paddingY: '10px' }}>
            <TextField
              className={styles.inputField}
              id="detail"
              name="detail"
              type="text"
              label="Label"
              variant="outlined"
              multiline
              rows={3}
              value={dataAdd.detail}
              error={!!errorsMessage?.detail}
              onChange={handleOnChange}
            />
            {errorsMessage?.detail && errorsMessage.detail.length > 0 && (
              <Box sx={{ marginTop: '14px' }}>
                <ErrorMessage message={errorsMessage.detail[0]} />
              </Box>
            )}
          </Box>
        </Stack>
        <Stack flexDirection="row" alignItems="center" sx={{ height: '104px' }}>
          <Box className={styles.labelContainer}>
            <Typography className={styles.textLabel}>
              {translate('safety.create.label.membership_type')}
            </Typography>
          </Box>
          <Box sx={{ marginLeft: '24px' }}>
            <FormControl className={styles.selectContainer}>
              <InputLabel id="safety-type-select-label">
                {translate('safety.create.label.membership_type.select')}
              </InputLabel>
              <Select
                labelId="safety-type-select-label"
                id="safety-type-select"
                label={translate('safety.create.label.membership_type.select')}
                className={styles.customSelectInput}
                onChange={handleSelectMembershipType}
                value={dataAdd.user_type.toString()}
                error={!!errorsMessage?.user_type}
              >
                {memberTypes.map((type: IUserType) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errorsMessage?.user_type && errorsMessage.user_type.length > 0 && (
              <Box sx={{ marginTop: '14px' }}>
                <ErrorMessage message={errorsMessage.user_type[0]} />
              </Box>
            )}
          </Box>
        </Stack>
        <Stack flexDirection="row" alignItems="center" sx={{ height: '170px' }}>
          <Box className={styles.labelContainer}>
            <Typography className={styles.textLabel}>
              {translate('safety.create.label.target_area')}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1, height: '100%' }}>
            <Stack
              flexDirection="row"
              alignItems="center"
              sx={{ height: '100%' }}
              className={styles.inputField}
            >
              <Box
                sx={{ minWidth: 'calc(200 / 736 * 100%)', marginRight: '8px' }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: NOTICE,
                    fontWeight: '600',
                    marginLeft: '8px',
                  }}
                >
                  {translate('safety.create.label.target_area.specific_area')}
                </Typography>
                <FormControl fullWidth sx={{ marginTop: '8px' }}>
                  <InputLabel id="area-select-label">
                    {translate('safety.create.label.target_area.area')}
                  </InputLabel>
                  <Select
                    labelId="area-select-label"
                    id="area-select"
                    value={value}
                    label={translate('safety.create.label.target_area.area')}
                    className={styles.customSelectInput}
                    onChange={handleSelectArea}
                    error={!!errorsMessage?.target_area}
                  >
                    {areas.map((area: IArea) => (
                      <MenuItem key={area.area_id} value={area.area_id}>
                        {area.area_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <SelectAllAreasField
                  isSelectAllAreas={isAllAreas}
                  setIsSelectAllAreas={handleSelectAllArea}
                />
                {errorsMessage?.target_area &&
                  errorsMessage.target_area.length > 0 && (
                    <Box sx={{ marginTop: '14px' }}>
                      <ErrorMessage message={errorsMessage.target_area[0]} />
                    </Box>
                  )}
              </Box>
              <Stack
                alignContent="flex-start"
                flexWrap="wrap"
                flexDirection="row"
                gap={1}
                sx={{
                  flexGrow: 1,
                  backgroundColor: '#F2F2F2',
                  borderRadius: '5px',
                  padding: '8px 12px',
                  height: '100%',
                  overflowY: 'auto',
                }}
              >
                {selectedAreas.map((area) => (
                  <Chip
                    key={area.area_id}
                    label={area.area_name}
                    sx={{
                      backgroundColor: LIGHT_PURPLE,
                      width: '100px',
                    }}
                    textSx={{
                      color: TYPOGRAPHY,
                      fontSize: '12px',
                      fontWeight: '500',
                    }}
                    onClick={() => handleDeleteArea(area.area_id)}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
        </Stack>
        <Stack flexDirection="row" alignItems="center" sx={{ height: '104px' }}>
          <Box className={styles.labelContainer}>
            <Typography className={styles.textLabel}>
              {translate('safety.create.label.membership_type')}
            </Typography>
          </Box>
          <Box sx={{ marginLeft: '10px' }}>
            <FormControlLabel
              control={<Switch checked={isOpen} />}
              label={translate('questionnaire.create_edit.item.publicity')}
              onClick={() => {
                setIsOpen(!isOpen);
                setDataAdd({
                  ...dataAdd,
                  is_open: !isOpen,
                });
              }}
            />
            {errorsMessage?.is_open && errorsMessage.is_open.length > 0 && (
              <Box sx={{ marginTop: '14px' }}>
                <ErrorMessage message={errorsMessage.is_open[0]} />
              </Box>
            )}
          </Box>
        </Stack>
        <Stack
          flexDirection="row"
          justifyContent="center"
          gap={5}
          sx={{ marginY: '40px' }}
        >
          <Button
            type="submit"
            label={translate('button.create')}
            sx={{
              width: '40px',
              height: '38px',
              borderRadius: 0,
              boxShadow: 0,
            }}
            textSx={{ fontSize: '14px', fontWeight: 'bold' }}
          />
          <TableButton
            label={translate('button_cancel')}
            onClick={handleBackButton}
          />
        </Stack>
      </FormProvider>
      <CreatePopup
        open={openCreateModal}
        onConfirm={() => navigate('/safety')}
        onClose={() => navigate('/safety')}
      />
    </Layout>
  );
};

export default CreateSafetyScreen;
